import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowRight, Calendar, ChevronDown } from 'lucide-react';
import moment from 'moment-timezone';
import { accent, bg, border, text } from '../../../../ux/Tasks/configs/designTokens';
import { getTimezoneAwareRanges } from '../../../../../core/hooks/ByDateFilter';
import InlineRangeCalendar from './InlineRangeCalendar';

// =============================================================================
// DateRangeMenu — modern single-trigger date range picker (2024-25 dashboard
// convention — Linear / Stripe / GA4 / Mixpanel / Plausible / shadcn-DRP).
//
// Implementation:
//   - Custom trigger button shows `<preset> · <range>`.
//   - Click opens a popover with: preset rail (left) + selected-dates row +
//     inline dual-month calendar (right).
//   - Inline calendar is a custom component (see `InlineRangeCalendar`) —
//     no AntD `<RangePicker>` (its hidden-trigger + popup pattern doesn't
//     propagate clicks reliably). Pure moment + plain HTML.
//   - Click outside the popover closes it; clicking a preset OR completing
//     a calendar pick also closes.
//
// API mirrors `<DateRangePicker>` so swap is a one-import change at the
// consumer; both consume the same `getTimezoneAwareRanges(timezone)` so
// "Last 7 Days" means the same thing across both surfaces.
// =============================================================================

const DEFAULT_PRESETS = [
  'Yesterday',
  'Last 7 Days',
  'This Month',
  'Last Month',
  'Last 30 Days',
  'This Quarter',
  'Last Quarter',
  'All Time',
];

const fmtShort = (d) =>
  d ? (moment.isMoment(d) ? d.clone() : moment.utc(d)).format('MMM D') : null;
const fmtFull = (d) =>
  d ? (moment.isMoment(d) ? d.clone() : moment.utc(d)).format('MMM D, YYYY') : '';

// Lenient parse — accept the canonical display format plus a few common
// hand-typed shapes. Returns a moment or null.
const PARSE_FORMATS = [
  'MMM D, YYYY',
  'MMMM D, YYYY',
  'YYYY-MM-DD',
  'MM/DD/YYYY',
  'M/D/YYYY',
  'MMM D YYYY',
  'MMM DD YYYY',
];
const parseTypedDate = (input) => {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  for (let i = 0; i < PARSE_FORMATS.length; i += 1) {
    const m = moment(trimmed, PARSE_FORMATS[i], true);
    if (m.isValid()) return m;
  }
  // Final lenient fallback (browser-locale / ISO).
  const lenient = moment(trimmed);
  return lenient.isValid() ? lenient : null;
};

const DateRangeMenu = ({
  value,
  onChange,
  rangeType,
  onRangeTypeChange,
  defaultDateRange,
  disabledDate,
  timezone = 'UTC',
  presets = DEFAULT_PRESETS,
  disabled = false,
  className = '',
  ariaLabel = 'Date range',
}) => {
  const [open, setOpen] = useState(false);
  const [editingField, setEditingField] = useState('start');
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const ranges = getTimezoneAwareRanges(timezone);

  // Mobile layout below 800px — popover pins to the bottom edge of the
  // viewport, presets become a horizontal scroll-pill row, calendar drops
  // to a single month so it fits without horizontal overflow.
  // (Threshold = the desktop popover's content width: rail 168 + dual-
  // month calendar ~540 + padding/border ~30 = ~740 + a comfort buffer.)
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const check = () => setIsMobile(window.innerWidth < 800);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // The canonical "All Time" preset is `2020-01-01 → today`, which doesn't
  // make sense in a campaign-flight context — it opens the calendar far
  // from the flight and most cells fall outside the disabled-date bounds.
  // Resolve "All Time" against `defaultDateRange` (the flight) when the
  // consumer provides one.
  const resolvePresetRange = (key) => {
    if (key === 'All Time' && defaultDateRange && defaultDateRange.length === 2) {
      return defaultDateRange;
    }
    return ranges[key];
  };

  // Reset editingField to 'start' whenever the popover opens fresh.
  useEffect(() => {
    if (open) setEditingField('start');
  }, [open]);

  // Defensively sort the value so end-before-start input never displays
  // as "May 3 → May 1" (consumer bugs, NaN-unix sorts on invalid moments,
  // etc. can produce reversed pairs).
  const rawHasValue =
    Array.isArray(value) && value.length === 2 && value[0] && value[1];
  const sortedValue = rawHasValue && moment.isMoment(value[0]) && moment.isMoment(value[1]) && value[0].isAfter(value[1])
    ? [value[1], value[0]]
    : value;
  const hasExplicitValue =
    Array.isArray(sortedValue) && sortedValue.length === 2 && sortedValue[0] && sortedValue[1];

  // Close on click outside.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const triggerLabel = (() => {
    const presetRange = rangeType ? resolvePresetRange(rangeType) : null;
    const range = hasExplicitValue
      ? sortedValue
      : presetRange && presetRange.length === 2
        ? presetRange
        : defaultDateRange;
    if (!range || !range[0] || !range[1]) return 'Select range';
    const dateLabel = `${fmtShort(range[0])} – ${fmtShort(range[1])}`;
    if (rangeType && rangeType !== 'Custom' && presets.includes(rangeType)) {
      return `${rangeType} · ${dateLabel}`;
    }
    return dateLabel;
  })();

  const handlePresetClick = (key) => {
    const range = resolvePresetRange(key);
    if (!range || range.length !== 2) return;
    if (onChange) onChange([range[0], range[1]]);
    if (onRangeTypeChange) onRangeTypeChange(key);
    setOpen(false);
  };

  // Calendar fires onChange after every cell click (not just second one).
  // We always update parent state immediately. Closing the popover happens
  // when:
  //   (a) editingField was 'end' before this pick (user just touched end),
  //       OR
  //   (b) the popover was already open with no prior pick and the user
  //       has now picked both fields in this session.
  const sessionPicksRef = useRef(0);
  useEffect(() => {
    if (open) sessionPicksRef.current = 0;
  }, [open]);

  const handleCalendarChange = (dates) => {
    if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) return;
    if (onChange) onChange(dates);
    if (onRangeTypeChange) {
      onRangeTypeChange(presetMatchKey(dates[0], dates[1]) || 'Custom');
    }
    sessionPicksRef.current += 1;
    // Close the popover after both fields touched in this open session, or
    // immediately when user explicitly came in to edit end only.
    if (sessionPicksRef.current >= 2 || editingField === 'end') {
      setOpen(false);
    }
  };

  const presetMatchKey = (start, end) =>
    presets.find((key) => {
      const r = resolvePresetRange(key);
      if (!r || r.length !== 2) return false;
      return start.isSame(r[0], 'day') && end.isSame(r[1], 'day');
    });

  const effectiveDisabledDate = (current) => {
    if (typeof disabledDate === 'function') return disabledDate(current);
    if (!defaultDateRange || defaultDateRange.length !== 2) return false;
    if (!current) return false;
    const [min, max] = defaultDateRange;
    if (!min || !max) return false;
    return current.isBefore(min, 'day') || current.isAfter(max, 'day');
  };

  // Effective range — what the trigger label and the calendar should both
  // reflect on open. Fallback chain: explicit value → resolved preset →
  // defaultDateRange. Same chain the trigger label uses.
  const presetEffectiveRange = rangeType ? resolvePresetRange(rangeType) : null;
  const effectiveRange = hasExplicitValue
    ? sortedValue
    : presetEffectiveRange && presetEffectiveRange.length === 2
      ? presetEffectiveRange
      : defaultDateRange || [null, null];
  const [selStart, selEnd] = effectiveRange;

  // Typed-input mirror state. Strings shadow the formatted dates; resync
  // whenever the underlying selection changes (preset click, calendar pick,
  // popover reopen).
  const [startTyped, setStartTyped] = useState('');
  const [endTyped, setEndTyped] = useState('');
  const startStamp = selStart && selStart.valueOf ? selStart.valueOf() : null;
  const endStamp = selEnd && selEnd.valueOf ? selEnd.valueOf() : null;
  useEffect(() => {
    setStartTyped(fmtFull(selStart));
    setEndTyped(fmtFull(selEnd));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startStamp, endStamp]);

  const commitTypedDate = (which) => {
    const raw = which === 'start' ? startTyped : endTyped;
    const parsed = parseTypedDate(raw);
    if (!parsed || effectiveDisabledDate(parsed)) {
      // Invalid or out of bounds — revert display to the current value.
      if (which === 'start') setStartTyped(fmtFull(selStart));
      else setEndTyped(fmtFull(selEnd));
      return;
    }
    // Auto-swap if the typed value crosses the other endpoint.
    let nextStart = which === 'start' ? parsed.clone() : (selStart || parsed.clone());
    let nextEnd = which === 'end' ? parsed.clone() : (selEnd || parsed.clone());
    if (nextStart.isAfter(nextEnd, 'day')) {
      const swapped = nextStart;
      nextStart = nextEnd;
      nextEnd = swapped;
    }
    if (onChange) onChange([nextStart.startOf('day'), nextEnd.endOf('day')]);
    if (onRangeTypeChange) {
      onRangeTypeChange(presetMatchKey(nextStart, nextEnd) || 'Custom');
    }
  };

  const popover = (
    <div
      className="ds-date-menu__panel"
      style={
        isMobile
          ? {
              position: 'fixed',
              left: 8,
              right: 8,
              bottom: 8,
              zIndex: 1050,
              background: bg.card,
              border: `1px solid ${border.light}`,
              borderRadius: 12,
              boxShadow:
                '0 4px 12px rgba(0,0,0,0.06), 0 -8px 32px rgba(0,0,0,0.12)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'calc(100vh - 16px)',
              overflowY: 'auto',
              fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
            }
          : {
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              // The app's sticky page header sits at z-index 10
              // (`platform/ux/Header/Header.js:282`). Keep the popover
              // BELOW it so the header isn't covered when the user scrolls
              // the trigger up through the header zone.
              zIndex: 5,
              background: bg.card,
              border: `1px solid ${border.light}`,
              borderRadius: 12,
              boxShadow:
                '0 4px 12px rgba(0,0,0,0.06), 0 16px 40px rgba(0,0,0,0.10)',
              display: 'flex',
              fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
            }
      }
    >
      <div
        className="ds-date-menu__rail"
        style={
          isMobile
            ? {
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 6,
                padding: '12px 12px 8px',
                borderBottom: `1px solid ${border.light}`,
                flexShrink: 0,
              }
            : {
                width: 168,
                padding: 8,
                borderRight: `1px solid ${border.light}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                flexShrink: 0,
              }
        }
      >
        {presets.map((key) => {
          const isActive = rangeType === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => handlePresetClick(key)}
              style={
                isMobile
                  ? {
                      appearance: 'none',
                      border: `1px solid ${isActive ? accent.primary : border.gray}`,
                      background: isActive ? 'rgba(24, 144, 255, 0.08)' : bg.card,
                      color: isActive ? accent.primary : text.darkAlt,
                      fontWeight: isActive ? 600 : 500,
                      fontSize: 12,
                      padding: '6px 12px',
                      borderRadius: 9999,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      fontFamily: 'inherit',
                    }
                  : {
                      appearance: 'none',
                      border: 'none',
                      background: isActive ? 'rgba(24, 144, 255, 0.08)' : 'transparent',
                      color: isActive ? accent.primary : text.darkAlt,
                      fontWeight: isActive ? 600 : 500,
                      fontSize: 13,
                      textAlign: 'left',
                      padding: '8px 12px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      lineHeight: 1.4,
                      transition: 'background 120ms ease',
                      fontFamily: 'inherit',
                    }
              }
              onMouseEnter={(e) => {
                if (!isMobile && !isActive)
                  e.currentTarget.style.background = 'rgba(15, 23, 42, 0.04)';
              }}
              onMouseLeave={(e) => {
                if (!isMobile && !isActive)
                  e.currentTarget.style.background = 'transparent';
              }}
            >
              {key}
            </button>
          );
        })}
      </div>
      <div
        className="ds-date-menu__calendar"
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        <div
          className="ds-date-menu__selection"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '12px 16px',
            borderBottom: `1px solid ${border.light}`,
            fontSize: 13,
            color: text.body,
          }}
        >
          <input
            type="text"
            value={startTyped}
            onChange={(e) => setStartTyped(e.target.value)}
            onFocus={() => setEditingField('start')}
            onBlur={() => commitTypedDate('start')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commitTypedDate('start');
                e.currentTarget.blur();
              } else if (e.key === 'Escape') {
                setStartTyped(fmtFull(selStart));
                e.currentTarget.blur();
              }
            }}
            placeholder="Start date"
            style={{
              appearance: 'none',
              padding: '6px 10px',
              border: `1px solid ${editingField === 'start' ? accent.primary : border.disabled}`,
              boxShadow: editingField === 'start' ? '0 0 0 2px rgba(24,144,255,0.16)' : 'none',
              borderRadius: 6,
              width: 140,
              textAlign: 'center',
              background: bg.hover,
              fontVariantNumeric: 'tabular-nums',
              fontFamily: 'inherit',
              fontSize: 13,
              color: text.body,
              outline: 'none',
            }}
          />
          <ArrowRight size={14} color={text.tertiary} strokeWidth={2} />
          <input
            type="text"
            value={endTyped}
            onChange={(e) => setEndTyped(e.target.value)}
            onFocus={() => setEditingField('end')}
            onBlur={() => commitTypedDate('end')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commitTypedDate('end');
                e.currentTarget.blur();
              } else if (e.key === 'Escape') {
                setEndTyped(fmtFull(selEnd));
                e.currentTarget.blur();
              }
            }}
            placeholder="End date"
            style={{
              appearance: 'none',
              padding: '6px 10px',
              border: `1px solid ${editingField === 'end' ? accent.primary : border.disabled}`,
              boxShadow: editingField === 'end' ? '0 0 0 2px rgba(24,144,255,0.16)' : 'none',
              borderRadius: 6,
              width: 140,
              textAlign: 'center',
              background: bg.hover,
              fontVariantNumeric: 'tabular-nums',
              fontFamily: 'inherit',
              fontSize: 13,
              color: text.body,
              outline: 'none',
            }}
          />
        </div>
        <InlineRangeCalendar
          value={selStart && selEnd ? [selStart, selEnd] : null}
          onChange={handleCalendarChange}
          defaultMonth={selStart || (defaultDateRange && defaultDateRange[0]) || moment()}
          disabledDate={effectiveDisabledDate}
          editingField={editingField}
          onEditingFieldChange={setEditingField}
          numberOfMonths={isMobile ? 1 : 2}
        />
      </div>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={`ds-date-menu ${className}`.trim()}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <button
        type="button"
        className="ds-date-menu__trigger"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        style={{
          appearance: 'none',
          background: bg.card,
          border: `1px solid ${border.disabled}`,
          borderRadius: 8,
          padding: '6px 12px',
          height: 32,
          fontSize: 13,
          fontWeight: 500,
          color: text.darkAlt,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.45 : 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: 'inherit',
          letterSpacing: '-0.005em',
        }}
      >
        <Calendar size={14} strokeWidth={2} color={text.tertiary} />
        <span>{triggerLabel}</span>
        <ChevronDown size={14} strokeWidth={2} color={text.tertiary} />
      </button>
      {open && popover}
    </div>
  );
};

DateRangeMenu.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  rangeType: PropTypes.string,
  onRangeTypeChange: PropTypes.func,
  defaultDateRange: PropTypes.array,
  disabledDate: PropTypes.func,
  timezone: PropTypes.string,
  presets: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

DateRangeMenu.PRESETS = DEFAULT_PRESETS;

export default DateRangeMenu;
